import { useId, type ReactNode } from "react";
import { TextField } from "@/components/TextField";
import { NumericField } from "@/components/NumericField";
import { SelectField } from "@/components/SelectField";
import { PhoneField } from "@/components/PhoneField";
import { AddressFieldset } from "@/components/AddressFieldset";
import {
  BondFilesField,
  type BondFilesPort,
} from "@/components/BondFilesField";
import { FieldMarker } from "@/components/FieldMarker";
import { ExtractedMarker } from "@/components/ExtractedReview";
import { withBondOptionLabels } from "@/lib/bondOptionLabels";
import {
  asAddress,
  asText,
  fieldKind,
  type BondFieldValue,
  type BondRenderField,
} from "@/lib/bondFields";
import type { BondDocumentRef } from "@/lib/brokerBonds";
import { US_STATE_OPTIONS } from "@/lib/usStates";

// BRK-S079 — the generic renderer for a CONFIG-driven bond field. The bond flow's
// middle steps are whatever the resolved product configuration says they are, so
// nothing here may branch on a product, a subproduct, or a field name: one
// mapping from the contract's field types onto the shared inputs, and every step
// renders from data.
//
// It is deliberately separate from FieldRenderer (BRK-S052). That one renders a
// product MANIFEST field against a submission field bag — different types
// (classCode, repeatable groups, tri-state booleans), different value shapes, and
// a submission id for the code typeahead. Merging them would mean one component
// serving two contracts, with every future change to either forced through both.
// The two do share every leaf input, which is where the duplication would matter.
//
// Value shapes, keyed by the field's `path` (fields step) or `key` (final step):
//   text/textarea/date/select → string · currency/number/percent → number
//   address → AddressValue · phone → string (the formatted display value)
// `undefined` means "absent" — an unanswered optional is never written as "".

export function BondFieldRenderer({
  field,
  value,
  onChange,
  error,
  pendingReview = false,
  files,
  unitControl,
}: {
  field: BondRenderField;
  value: BondFieldValue | undefined;
  onChange: (value: BondFieldValue | undefined) => void;
  /** Why this value isn't an answer (BRK-S091) — rendered inline on the offending
   *  control, so a broker fixes it in place rather than reading a step summary. */
  error?: string | null;
  /**
   * BRK-S120 — this value came from an uploaded document and still awaits the
   * broker's confirmation. Rendered in the same annotation slot the on-file
   * notice uses, so ALL TEN controls carry it without a per-branch change (the
   * `onFileNotice` precedent, and the reason that slot is a wrapper).
   */
  pendingReview?: boolean;
  /**
   * BRK-S145 — what a `files` field needs to do its two writes, supplied by the
   * page (which owns the order id, the served rules and the filed-or-draft
   * question). ABSENT is a legitimate state and not an oversight: a surface
   * that renders config fields with no order behind them cannot accept an
   * upload, so the field says so rather than offering a drop zone that would
   * fail — see the `files` case.
   */
  files?: BondFilesPort;
  /**
   * BRK-S181 — a control that is a NUMERIC field's unit, drawn inside its box
   * as the suffix in place of the static `$` / `%`. A slot rather than a
   * branch: the renderer still never asks which field it is drawing; the
   * paired composite that knows supplies the toggle. Ignored by every
   * non-numeric control.
   */
  unitControl?: ReactNode;
}) {
  const help = field.help ? (
    <p className="text-xs text-brk-dim">{field.help}</p>
  ) : null;
  const inlineError = error ?? undefined;
  // Branches that hand-roll their own markup (checkbox, textarea) need stable
  // ids to point aria-describedby and htmlFor at. The shared inputs own theirs
  // internally.
  const generatedId = useId();
  const errorId = `${generatedId}-error`;
  const controlId = `${generatedId}-control`;

  // The plain-text control, shared by the `text` case and an options-less
  // `select` (BRK-S098) — one definition, so both render the same thing rather
  // than two near-copies. It is deliberately NO LONGER the unknown-type
  // fallback: see the `unsupported` case.
  const textInput = () => (
    <div className="flex flex-col gap-1">
      <TextField
        label={field.label}
        // D-10 rule 1 matches the declared type AND the server's `format`.
        // `email` is a format rather than a control type, so the control stays
        // the text one and only its input type changes — enough for the @
        // keyboard on a phone and the browser's own affordances, without the
        // wholesale control swap `state` performs (BRK-S111).
        type={field.format === "email" ? "email" : "text"}
        value={asText(value)}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : e.target.value)
        }
        required={field.required}
        optional={!field.required}
        error={inlineError}
      />
      {help}
    </div>
  );

  // The ONE control decision, made in `lib/bondFields.ts` so the validation
  // rules and the step's blocking rule ask the same question this switch
  // answers (BRK-S142).
  const kind = fieldKind(field);

  // BRK-S105 — the write-only-PII on-file line, rendered for whatever control
  // the field ended up with. Wrapping the control rather than threading the
  // line through all ten branches keeps the two concerns separate: the switch
  // decides what the input IS, this decides whether it carries an indicator.
  const control = () => {
    // A `state` format is a CLOSED set, so it gets the shared picker whatever
    // control the config names — the same 2-letter list every other state field in
    // the portal uses (BRK-S050), rather than free text a broker can mistype.
    if (field.format === "state") {
      return (
        <div className="flex flex-col gap-1">
          <SelectField
            label={field.label}
            placeholder="Select a state…"
            options={US_STATE_OPTIONS}
            value={asText(value).toUpperCase()}
            onChange={(e) =>
              onChange(e.target.value === "" ? undefined : e.target.value)
            }
            required={field.required}
            error={inlineError}
          />
          {help}
        </div>
      );
    }

    switch (kind) {
      case "address":
        return (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-brk-text">{field.label}</p>
            {/* Filled out to the full shape: a controlled fieldset needs all four
              members as strings, and a RESUMED bag is whatever the server
              stored (BRK-S088). */}
            <AddressFieldset
              bare
              groupLabel={field.label}
              value={asAddress(value)}
              onChange={(a) => onChange(a)}
              error={inlineError}
            />
            {help}
          </div>
        );

      case "phone":
        return (
          <div className="flex flex-col gap-1">
            <PhoneField
              label={field.label}
              value={asText(value)}
              onChange={(v) => onChange(v === "" ? undefined : v)}
              required={field.required}
              optional={!field.required}
              error={inlineError}
            />
            {help}
          </div>
        );

      case "select": {
        // A select with nothing to choose from is a control the broker cannot use
        // — and cannot get past when it's required. Whatever the contract failed
        // to send, degrade to free text so there is always a way forward
        // (BRK-S098). This is a defense, not a fix: a field whose values are ids
        // still needs its real options to be answered CORRECTLY, which is a
        // contract-side repair. Note the difference from the `unsupported` case:
        // here the CONTROL is known and its choices are missing, so free text
        // still collects the right KIND of value; there, nothing is known about
        // what a valid value even looks like.
        // A plain string means the value IS the label; a pair separates them for
        // a field whose value is an identifier (BRK-S100). Normalising here means
        // the rest of this branch never asks which spelling it got.
        //
        // `withBondOptionLabels` runs FIRST (BRK-S173), so an option this client
        // has a better word for reads as that word — "Federal", not "Fed" — while
        // the value written back is still the configuration's. It is the same
        // call the read surfaces make through `formatBondValue`, so the control
        // and the record cannot disagree about one option's wording.
        const options = (withBondOptionLabels(field.options) ?? []).map((o) =>
          typeof o === "string" ? { value: o as string | number, label: o } : o,
        );
        if (options.length === 0) return textInput();
        return (
          <div className="flex flex-col gap-1">
            <SelectField
              label={field.label}
              placeholder="Select…"
              // The contract sends plain strings; the value IS the label, so a
              // config can add an option without a client release.
              options={options.map((o) => ({
                value: String(o.value),
                label: o.label,
              }))}
              // A stored value may be a number (an option id), so compare as text
              // — otherwise a prefilled `1` matches nothing and renders blank,
              // which is exactly what a broker saw for entityType.
              value={value === undefined ? "" : String(value)}
              onChange={(e) => {
                if (e.target.value === "") return onChange(undefined);
                // Send back the option's ORIGINAL value: a DOM select value is
                // always a string, and upstream rejects "1" with "must be a valid
                // number". Nothing offline would show that.
                const chosen = options.find(
                  (o) => String(o.value) === e.target.value,
                );
                onChange(chosen ? chosen.value : e.target.value);
              }}
              required={field.required}
              error={inlineError}
            />
            {help}
          </div>
        );
      }

      case "checkbox":
        return (
          <div className="flex flex-col gap-1">
            <label className="flex items-start gap-2 text-sm text-brk-text">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-brk-border"
                checked={value === true}
                onChange={(e) => onChange(e.target.checked)}
                aria-invalid={inlineError ? true : undefined}
                aria-describedby={inlineError ? errorId : undefined}
              />
              <span>{field.label}</span>
              {/* Sibling of the label text and aria-hidden, so a required
                  attestation carries the same "*" every other required control
                  does without that marker joining the accessible name. */}
              <FieldMarker required={field.required} />
            </label>
            {inlineError && (
              <p id={errorId} className="text-sm text-brk-danger">
                {inlineError}
              </p>
            )}
            {help}
          </div>
        );

      case "textarea":
        // Laid out like TextField/SelectField rather than as a wrapping <label>:
        // the required/optional marker has to be a SIBLING of the <label> and
        // aria-hidden, which is FieldMarker's stated contract. Inlining the word
        // "(optional)" inside the label made this control's accessible name
        // "Notes (optional)" where the same field rendered as text was "Notes"
        // — a convention that held for eight of ten controls (BRK-S111).
        return (
          <div className="flex w-full flex-col gap-1.5 text-left">
            <span className="flex items-baseline gap-1.5">
              <label
                htmlFor={controlId}
                className="text-sm font-medium text-brk-text"
              >
                {field.label}
              </label>
              <FieldMarker
                required={field.required}
                optional={!field.required}
              />
            </span>
            <textarea
              id={controlId}
              rows={3}
              required={field.required}
              value={asText(value)}
              onChange={(e) =>
                onChange(e.target.value === "" ? undefined : e.target.value)
              }
              aria-invalid={inlineError ? true : undefined}
              aria-describedby={inlineError ? errorId : undefined}
              className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
            />
            {inlineError && (
              <p id={errorId} className="text-sm text-brk-danger">
                {inlineError}
              </p>
            )}
            {help}
          </div>
        );

      case "currency":
      case "number":
      case "percent":
        return (
          <div className="flex flex-col gap-1">
            {/* Three DIFFERENT controls, not one numeric input with different
                bounds: a currency field groups thousands and carries a `$`, a
                percent carries a `%` and its own 0–100 default, and a plain
                number is a count. See NumericField for why grouping forces a
                text input (BRK-S142). */}
            <NumericField
              kind={kind}
              label={field.label}
              value={typeof value === "number" ? value : undefined}
              onChange={onChange}
              required={field.required}
              optional={!field.required}
              error={inlineError}
              min={field.min}
              max={field.max}
              unitControl={unitControl}
            />
            {help}
          </div>
        );

      case "date":
        return (
          <div className="flex flex-col gap-1">
            <TextField
              label={field.label}
              type="date"
              value={asText(value)}
              onChange={(e) =>
                onChange(e.target.value === "" ? undefined : e.target.value)
              }
              required={field.required}
              optional={!field.required}
              error={inlineError}
            />
            {help}
          </div>
        );

      case "text":
        return textInput();

      case "files":
        // No port means no order to attach to. Named rather than blank, and
        // rendered as the same guarded amber card an unrenderable field gets:
        // "nothing happened when I dropped my file" is the one outcome worse
        // than "you can't do that here yet".
        if (!files) {
          return (
            <div className="flex flex-col gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-900">
                {field.label}
              </p>
              <p role="alert" className="text-sm text-amber-900">
                Documents can’t be attached here — start or open the bond
                request first.
              </p>
            </div>
          );
        }
        return (
          <BondFilesField
            field={field}
            value={value}
            onChange={(documents: BondDocumentRef[] | undefined) =>
              onChange(documents)
            }
            port={files}
            error={inlineError}
          />
        );

      case "unsupported":
        // NOT a text box (BRK-S142). See `fieldKind` for why the old
        // degrade-to-text fallback was the wrong answer, and D-10 rule 1 for
        // whose rule that is.
        return <UnsupportedField field={field} />;
    }
    // No `default:` — the switch is exhaustive over `BondFieldKind`, so a
    // control type added to the union fails to COMPILE here rather than
    // silently falling through to a text box the way it used to.
  };

  const rendered = control();
  if (!field.onFileNotice && !pendingReview) return rendered;
  return (
    <div className="flex flex-col gap-1">
      {rendered}
      {/* Not an error and not the config's own help text: it is the server
          telling the broker they can skip this field. Same dim voice as help,
          its own element so a test (and a screen reader) can find it. */}
      {field.onFileNotice && (
        <p className="text-xs text-brk-dim">{field.onFileNotice}</p>
      )}
      {/* Both, when both apply: an SSN PMS holds and a value a document
          proposed are different facts, and showing one INSTEAD of the other
          would hide whichever lost. */}
      {pendingReview && <ExtractedMarker />}
    </div>
  );
}

/**
 * A field this build cannot draw a control for (BRK-S142).
 *
 * NAMED AND GUARDED, never a text box. D-10 rule 1: "If a control cannot be
 * matched to the type, that is a contract gap to fix, not a reason to fall back
 * to a text box." A text box accepts anything, so it invites an answer the
 * server will reject several layers away — and it makes the gap invisible, which
 * is the part that matters: nobody ever finds out the portal is a version behind.
 *
 * `role="alert"` and the field's own label, so the broker can say WHICH field
 * when they call, and the declared type in the supporting line, so we can too.
 * The step's footer is what stops them proceeding past it (`unsupportedFields`);
 * this card is what explains why.
 */
function UnsupportedField({ field }: { field: BondRenderField }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-4">
      <p className="text-sm font-medium text-amber-900">{field.label}</p>
      <p role="alert" className="text-sm text-amber-900">
        This portal can’t show this field yet — reload the page to pick up the
        latest version. If it still doesn’t appear, contact us and we’ll finish
        it with you.
      </p>
      {/* The declared type, for the support conversation this card starts. Dim
          and secondary: it means nothing to a broker and everything to us. */}
      <p className="text-xs text-amber-800">
        Unsupported field type: <span className="font-mono">{field.type}</span>
      </p>
    </div>
  );
}
